import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Statistic, Table, Tag, Button, Spin, Alert, Modal, Form, Input, InputNumber, Divider, Space, Result, Tooltip, message } from 'antd';
import {
  ShopOutlined,
  WalletOutlined,
  TeamOutlined,
  DollarOutlined,
  CopyOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  BankOutlined,
  PlusOutlined,
  EditOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { t } from '../utils/i18n';
import { BASE_URL } from '../utils/apiUrl';
import { copyToClipboard } from '../utils/clipboard';

const MerchantCenter = () => {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [merchantInfo, setMerchantInfo] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [bankCard, setBankCard] = useState(null);
  const [withdrawalRecords, setWithdrawalRecords] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [withdrawModalVisible, setWithdrawModalVisible] = useState(false);
  const [editingBankCard, setEditingBankCard] = useState(false);
  const [form] = Form.useForm();
  const [withdrawForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('user_token');

      if (!token) {
        navigate('/login');
        return;
      }

      // 获取商户信息（API 会检查商户状态）
      const merchantRes = await fetch(`${BASE_URL}/api/merchant`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const merchantData = await merchantRes.json();

      if (merchantData.code !== 0) {
        // 商户被禁用或其他错误
        console.log('商户状态检查失败:', merchantData.message);
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      // 商户状态正常
      setMerchantInfo(merchantData.data);

      // 获取可参与的活动列表
      try {
        const campaignsRes = await fetch(`${BASE_URL}/api/referral/merchant-campaigns`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const campaignsData = await campaignsRes.json();
        if (campaignsData.code === 0) {
          setCampaigns(campaignsData.data || []);
        }
      } catch (e) {
        console.log('获取活动列表失败', e);
      }

      // 获取下线列表
      try {
        const referralRes = await fetch(`${BASE_URL}/api/merchant/referrals`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const referralData = await referralRes.json();
        if (referralData.code === 0) {
          setReferrals(referralData.data || []);
        }
      } catch (e) {
        console.log('获取下线列表失败', e);
      }

      // 获取佣金记录
      try {
        const commissionRes = await fetch(`${BASE_URL}/api/merchant/commissions`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const commissionData = await commissionRes.json();
        if (commissionData.code === 0) {
          setCommissions(commissionData.data || []);
        }
      } catch (e) {
        console.log('获取佣金记录失败', e);
      }

      // 获取银行卡信息
      try {
        const bankRes = await fetch(`${BASE_URL}/api/merchant/bank-card`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const bankData = await bankRes.json();
        if (bankData.code === 0) {
          setBankCard(bankData.data);
        }
      } catch (e) {
        console.log('获取银行卡信息失败', e);
      }

      // 获取提现记录
      try {
        const withdrawRes = await fetch(`${BASE_URL}/api/merchant/withdrawals`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const withdrawData = await withdrawRes.json();
        if (withdrawData.code === 0) {
          setWithdrawalRecords(withdrawData.data || []);
        }
      } catch (e) {
        console.log('获取提现记录失败', e);
      }
    } catch (error) {
      console.error('获取数据失败', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取商户邀请链接（短链接格式 /r/:code）
  const getMerchantLink = () => {
    const baseUrl = window.location.origin;
    const code = merchantInfo?.merchant_code || '';
    return code ? `${baseUrl}/r/${code}` : '';
  };

  // 复制链接
  const copyLink = async (link) => {
    const success = await copyToClipboard(link);
    if (success) {
      messageApi.success(t('copy_success'));
    }
  };

  // 打开银行卡弹窗
  const openBankCardModal = () => {
    if (bankCard) {
      form.setFieldsValue({
        bank_name: bankCard.bank_name,
        bank_branch: bankCard.bank_branch,
        account_name: bankCard.account_name,
        account_number: bankCard.account_number,
        remark: bankCard.remark || '',
      });
      setEditingBankCard(true);
    } else {
      form.resetFields();
      setEditingBankCard(false);
    }
    setModalVisible(true);
  };

  // 保存银行卡信息
  const handleSaveBankCard = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const token = localStorage.getItem('user_token');
      const res = await fetch(`${BASE_URL}/api/merchant/bank-card`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });
      const data = await res.json();
      if (data.code === 0) {
        messageApi.success(t('save'));
        setBankCard(data.data);
        setModalVisible(false);
      } else {
        messageApi.error(data.message || t('save'));
      }
    } catch (error) {
      console.error('保存银行卡失败', error);
    } finally {
      setSubmitting(false);
    }
  };

  // 打开提现弹窗
  const openWithdrawModal = () => {
    if (!bankCard) {
      messageApi.warning(t('add_bank_card'));
      openBankCardModal();
      return;
    }
    const availableBalance = (merchantInfo?.pending_commission || 0) / 100000; // 转为美元
    withdrawForm.setFieldsValue({ amount: availableBalance });
    setWithdrawModalVisible(true);
  };

  // 提交提现申请
  const handleWithdraw = async () => {
    try {
      const values = await withdrawForm.validateFields();
      const availableBalance = (merchantInfo?.pending_commission || 0) / 100000; // 转为美元
      const minWithdrawal = merchantInfo?.min_withdrawal || 10;
      if (values.amount > availableBalance) {
        messageApi.error(t('amount_exceeds_balance'));
        return;
      }
      if (values.amount < minWithdrawal) {
        messageApi.error(`${t('amount_below_min')} $${minWithdrawal}`);
        return;
      }
      setSubmitting(true);
      const token = localStorage.getItem('user_token');
      const res = await fetch(`${BASE_URL}/api/merchant/withdraw`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount: parseFloat(values.amount) }),
      });
      const data = await res.json();
      if (data.code === 0) {
        messageApi.success(t('withdraw_success'));
        setWithdrawModalVisible(false);
        fetchData(); // 刷新数据
      } else {
        messageApi.error(data.message || t('withdraw_failed'));
      }
    } catch (error) {
      console.error('提现失败', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  // 商户权限被禁用
  if (accessDenied) {
    return (
      <div style={{ padding: '24px' }}>
        <Result
          status="403"
          title={t('merchant_access_denied')}
          subTitle={t('merchant_access_denied_desc')}
          extra={
            <Button type="primary" onClick={() => navigate('/')}>
              {t('back_to_home')}
            </Button>
          }
        />
      </div>
    );
  }

  const referralColumns = [
    {
      title: t('email'),
      dataIndex: 'email',
      key: 'email',
      render: (text) => text || '-',
    },
    {
      title: t('registered_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status === 'active' ? t('active_status') : t('pending_status')}
        </Tag>
      ),
    },
    {
      title: t('consumption_amount'),
      dataIndex: 'total_spent',
      key: 'total_spent',
      render: (amount) => `$${amount?.toFixed(2) || '0.00'}`,
    },
  ];

  const commissionColumns = [
    {
      title: t('time'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: t('type'),
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const config = {
          referral: { text: t('referral_type'), color: 'blue' },
          consumption: { text: t('consumption_type'), color: 'green' },
        };
        const item = config[type] || { text: t('my_commission'), color: 'default' };
        return <Tag color={item.color}>{item.text}</Tag>;
      },
    },
    {
      title: t('referee_email'),
      dataIndex: 'referee_email',
      key: 'referee_email',
      render: (text) => text || '-',
    },
    {
      title: t('consumption_amount'),
      dataIndex: 'order_amount',
      key: 'order_amount',
      render: (amount) => amount > 0 ? `$${parseFloat(amount).toFixed(2)}` : '-',
    },
    {
      title: t('my_commission'),
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
          +${parseFloat(amount).toFixed(2)}
        </span>
      ),
    },
    {
      title: t('commission_rate'),
      dataIndex: 'rate',
      key: 'rate',
      render: (rate) => `${(rate * 100).toFixed(0)}%`,
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colorMap = {
          pending: 'orange',
          paid: 'green',
          cancelled: 'red',
        };
        const textMap = {
          pending: t('commission_pending'),
          paid: t('commission_paid'),
          cancelled: t('commission_cancelled'),
        };
        return <Tag color={colorMap[status]}>{textMap[status]}</Tag>;
      },
    },
  ];

  const withdrawalColumns = [
    {
      title: t('time'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: t('withdraw_amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
          -${amount?.toFixed(2) || '0.00'}
        </span>
      ),
    },
    {
      title: t('bank_card_info'),
      key: 'bank_info',
      render: (_, record) => (
        <span>
          {record.bank_name} ({record.account_number?.slice(-4)})
        </span>
      ),
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colorMap = {
          pending: 'orange',
          approved: 'blue',
          paid: 'green',
          rejected: 'red',
        };
        const textMap = {
          pending: t('withdrawal_pending'),
          approved: t('withdrawal_approved'),
          paid: t('withdrawal_paid'),
          rejected: t('withdrawal_rejected'),
        };
        return <Tag color={colorMap[status]}>{textMap[status]}</Tag>;
      },
    },
    {
      title: t('processed_at'),
      dataIndex: 'processed_at',
      key: 'processed_at',
      render: (text) => text ? new Date(text).toLocaleString() : '-',
    },
    {
      title: t('remark'),
      dataIndex: 'remark',
      key: 'remark',
      render: (text) => text || '-',
    },
  ];

  const merchantLink = getMerchantLink();

  return (
    <div style={{ padding: '24px' }}>
      {contextHolder}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Alert
            message={t('merchant_center')}
            description={t('merchant_desc')}
            type="info"
            showIcon
          />
        </Col>
      </Row>

      {/* 商户邀请链接 */}
      <Card title={t('merchant_invite_link')} style={{ marginBottom: 24 }}>
        {merchantLink ? (
          <div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontSize: 16, fontWeight: 500 }}>{t('promo_code')}：{merchantInfo?.merchant_code}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ color: '#666', flexShrink: 0 }}>{t('invite_link')}：</span>
              <input
                type="text"
                value={merchantLink}
                readOnly
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  background: '#fafafa',
                  fontSize: 14,
                }}
              />
              <Button type="primary" icon={<CopyOutlined />} onClick={() => copyLink(merchantLink)}>
                {t('copy_link')}
              </Button>
            </div>
            <div style={{ marginTop: 12, color: '#888', fontSize: 12 }}>
              {t('merchant_link_hint')}
            </div>
          </div>
        ) : (
          <div style={{ color: '#999' }}>{t('cannot_generate_link')}</div>
        )}
      </Card>

      {/* 可参与的平台活动 */}
      {campaigns.length > 0 && (
        <Card title={t('available_campaigns')} style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 12, color: '#888', fontSize: 12 }}>
            {t('campaign_hint')}
          </div>
          {campaigns.map(campaign => {
            const campaignLink = `${window.location.origin}/c/${campaign.campaign_code}?ref=${merchantInfo?.merchant_code}`;
            const startTime = campaign.start_time ? campaign.start_time.split('T')[0] : '-';
            const endTime = campaign.end_time ? campaign.end_time.split('T')[0] : '-';
            const now = new Date();
            const endDate = campaign.end_time ? new Date(campaign.end_time) : null;
            const isExpired = endDate && endDate < now;
            const remainingSlots = campaign.max_recipients > 0 ? campaign.max_recipients - (campaign.current_count || 0) : t('unlimited');

            return (
              <Card
                key={campaign.id}
                type="inner"
                title={campaign.name}
                size="small"
                style={{ marginBottom: 12, opacity: isExpired ? 0.6 : 1 }}
                extra={
                  isExpired ? (
                    <Tag color="default">{t('ended')}</Tag>
                  ) : campaign.status === 'active' ? (
                    <Tag color="green">{t('ongoing')}</Tag>
                  ) : (
                    <Tag color="orange">{t('closed')}</Tag>
                  )
                }
              >
                <Row gutter={[16, 8]}>
                  <Col span={12}>
                    <span style={{ color: '#666' }}>{t('campaign_bonus')}：</span>
                    <span style={{ color: '#1890ff', fontWeight: 500 }}>${campaign.bonus_amount}</span>
                  </Col>
                  <Col span={12}>
                    <span style={{ color: '#666' }}>{t('bonus_wallet')}：</span>
                    <span>{campaign.merchant_wallet_type === 'withdrawable' ? t('withdrawable_account') : t('consumption_account')}</span>
                  </Col>
                  <Col span={12}>
                    <span style={{ color: '#666' }}>{t('min_recharge_required')}：</span>
                    <span>{campaign.condition_min > 0 ? `$${campaign.condition_min}` : t('no_requirement')}</span>
                  </Col>
                  <Col span={12}>
                    <span style={{ color: '#666' }}>{t('remaining_slots')}：</span>
                    <span>{remainingSlots}</span>
                  </Col>
                  <Col span={24}>
                    <span style={{ color: '#666' }}>{t('campaign_time')}：</span>
                    <span>{startTime} ~ {endTime}</span>
                  </Col>
                  {!isExpired && campaign.status === 'active' && (
                    <Col span={24} style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ color: '#666', flexShrink: 0 }}>{t('campaign_link')}：</span>
                        <input
                          type="text"
                          value={campaignLink}
                          readOnly
                          style={{
                            flex: 1,
                            padding: '6px 10px',
                            border: '1px solid #d9d9d9',
                            borderRadius: 4,
                            background: '#fafafa',
                            fontSize: 13,
                          }}
                        />
                        <Button size="small" icon={<CopyOutlined />} onClick={() => copyLink(campaignLink)}>
                          {t('copy')}
                        </Button>
                      </div>
                    </Col>
                  )}
                </Row>
              </Card>
            );
          })}
        </Card>
      )}

      {/* 收益统计 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('total_commission')}
              value={(merchantInfo?.total_commission || 0) / 100000}
              precision={2}
              prefix={<DollarOutlined style={{ color: '#1890ff' }} />}
              suffix="$"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('withdrawable_balance')}
              value={(merchantInfo?.pending_commission || 0) / 100000}
              precision={2}
              prefix={<WalletOutlined style={{ color: '#faad14' }} />}
              suffix="$"
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('referral_count')}
              value={referrals.length}
              prefix={<TeamOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('commission_rate')}
              value={(merchantInfo?.commission_rate || 0.1) * 100}
              precision={0}
              prefix={<ArrowUpOutlined style={{ color: '#eb2f96' }} />}
              suffix="%"
              valueStyle={{ color: '#eb2f96' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 银行卡与提现 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {/* 银行卡信息 */}
        <Col xs={24} md={12}>
          <Card
            title={
              <Space>
                <BankOutlined />
                <span>{t('bank_card_info')}</span>
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={bankCard ? <EditOutlined /> : <PlusOutlined />}
                size="small"
                onClick={openBankCardModal}
              >
                {bankCard ? t('edit_bank_card') : t('add_bank_card')}
              </Button>
            }
          >
            {bankCard ? (
              <div>
                <Row gutter={16}>
                  <Col span={12}>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>{t('bank_name')}</div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{bankCard.bank_name}</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>{t('bank_branch')}</div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{bankCard.bank_branch}</div>
                    </div>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>{t('account_name')}</div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{bankCard.account_name}</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>{t('account_number')}</div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{bankCard.account_number?.replace(/(\d{4})(?=\d)/g, '$1 ')}</div>
                    </div>
                  </Col>
                </Row>
                {bankCard.remark && (
                  <Row gutter={16}>
                    <Col span={24}>
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>{t('remark')}</div>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{bankCard.remark}</div>
                      </div>
                    </Col>
                  </Row>
                )}

              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>
                <BankOutlined style={{ fontSize: 48, marginBottom: 12, color: '#d9d9d9' }} />
                <div>{t('no_bank_card')}</div>
                <Button type="link" onClick={openBankCardModal}>{t('add_now')}</Button>
              </div>
            )}
          </Card>
        </Col>

        {/* 佣金提现 */}
        <Col xs={24} md={12}>
          <Card
            title={
              <Space>
                <DollarOutlined />
                <span>{t('commission_withdraw')}</span>
              </Space>
            }
            extra={
                <Tooltip title={
                !bankCard 
                  ? t('add_bank_card') 
                  : (merchantInfo?.pending_commission || 0) / 100000 < (merchantInfo?.min_withdrawal || 10)
                    ? `${t('amount_below_min')} $${merchantInfo?.min_withdrawal || 10}`
                    : ''
              }>
                <Button
                  type="primary"
                  icon={<ArrowDownOutlined />}
                  onClick={openWithdrawModal}
                  disabled={!bankCard || (merchantInfo?.pending_commission || 0) / 100000 < (merchantInfo?.min_withdrawal || 10)}
                >
                  {t('apply_withdraw')}
                </Button>
              </Tooltip>
            }
          >
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title={t('withdrawable_balance')}
                  value={(merchantInfo?.pending_commission || 0) / 100000}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: '#52c41a', fontSize: 20 }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={t('min_withdraw')}
                  value={merchantInfo?.min_withdrawal || 10}
                  precision={0}
                  prefix="$"
                  valueStyle={{ color: '#faad14', fontSize: 20 }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={t('applied')}
                  value={withdrawalRecords.filter(r => r.status === 'pending' || r.status === 'approved').reduce((sum, r) => sum + r.amount, 0)}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: '#faad14', fontSize: 20 }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={t('total_withdrawn')}
                  value={merchantInfo?.withdrawn_commission || 0}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: '#1890ff', fontSize: 20 }}
                />
              </Col>
            </Row>
            <Divider style={{ margin: '16px 0' }} />
            <div style={{ color: '#666', fontSize: 13 }}>
              <div style={{ marginBottom: 6 }}>
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                {t('withdraw_hint_1')}
              </div>
              <div style={{ marginBottom: 6 }}>
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                {t('withdraw_hint_2')}
              </div>
              <div>
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                {t('withdraw_hint_3')}
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 下线客户列表 */}
      <Card title={t('referral_list')} style={{ marginBottom: 24 }}>
        {referrals.length > 0 ? (
          <Table
            dataSource={referrals}
            columns={referralColumns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            {t('no_referrals_hint')}
          </div>
        )}
      </Card>

      {/* 佣金记录 */}
      <Card title={t('commission_records')} style={{ marginBottom: 24 }}>
        {commissions.length > 0 ? (
          <Table
            dataSource={commissions}
            columns={commissionColumns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            {t('no_commission_hint')}
          </div>
        )}
      </Card>

      {/* 提现记录 */}
      <Card title={t('withdrawal_records')}>
        {withdrawalRecords.length > 0 ? (
          <Table
            dataSource={withdrawalRecords}
            columns={withdrawalColumns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            {t('no_withdrawal_records')}
          </div>
        )}
      </Card>

      {/* 银行卡信息弹窗 */}
      <Modal
        title={editingBankCard ? t('edit_bank_card') : t('add_bank_card')}
        open={modalVisible}
        onOk={handleSaveBankCard}
        onCancel={() => setModalVisible(false)}
        confirmLoading={submitting}
        okText={t('save')}
        cancelText={t('cancel')}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="bank_name"
            label={t('bank_name')}
            rules={[{ required: true, message: t('enter_bank_name') }]}
          >
            <Input placeholder={t('enter_bank_name')} />
          </Form.Item>
          <Form.Item
            name="bank_branch"
            label={t('bank_branch')}
            rules={[{ required: true, message: t('enter_bank_branch') }]}
          >
            <Input placeholder={t('enter_bank_branch')} />
          </Form.Item>
          <Form.Item
            name="account_name"
            label={t('account_name')}
            rules={[{ required: true, message: t('enter_account_name') }]}
          >
            <Input placeholder={t('enter_account_name')} />
          </Form.Item>
          <Form.Item
            name="account_number"
            label={t('account_number')}
            rules={[
              { required: true, message: t('enter_account_number') },
              { pattern: /^\d{16,19}$/, message: t('account_number_invalid') }
            ]}
          >
            <Input placeholder={t('enter_account_number')} maxLength={19} />
          </Form.Item>
          <Form.Item
            name="remark"
            label={t('remark')}
          >
            <Input.TextArea placeholder={t('enter_remark')} rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 提现申请弹窗 */}
      <Modal
        title={t('withdrawal_apply')}
        open={withdrawModalVisible}
        onOk={handleWithdraw}
        onCancel={() => setWithdrawModalVisible(false)}
        confirmLoading={submitting}
        okText={t('confirm')}
        cancelText={t('cancel')}
      >
        <Alert
          message={t('withdraw')}
          description={t('withdrawal_desc')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={withdrawForm} layout="vertical">
          <Form.Item
            name="amount"
            label={`${t('withdraw_amount')} (${t('min')} $${merchantInfo?.min_withdrawal || 10})`}
            rules={[
              { required: true, message: t('enter_amount') },
              { validator: (_, value) => {
                const minWithdrawal = merchantInfo?.min_withdrawal || 10;
                const maxWithdrawal = (merchantInfo?.pending_commission || 0) / 100000; // 转为美元
                if (value && value > maxWithdrawal) {
                  return Promise.reject(t('amount_exceeds_balance'));
                }
                if (value && value < minWithdrawal) {
                  return Promise.reject(`${t('amount_below_min')} $${minWithdrawal}`);
                }
                return Promise.resolve();
              }}
            ]}
          >
            <InputNumber
              prefix="$"
              placeholder={t('enter_amount')}
              style={{ width: '100%' }}
              min={merchantInfo?.min_withdrawal || 10}
              max={(merchantInfo?.pending_commission || 0) / 100000}
              precision={2}
              controls={false}
            />
          </Form.Item>
        </Form>
        <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ color: '#666' }}>{t('withdrawable_balance')}</span>
            <span style={{ fontWeight: 500 }}>${((merchantInfo?.pending_commission || 0) / 100000).toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ color: '#666' }}>{t('min_withdraw')}</span>
            <span style={{ fontWeight: 500, color: '#faad14' }}>${merchantInfo?.min_withdrawal || 10}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#666' }}>{t('receiving_bank')}</span>
            <span style={{ fontWeight: 500 }}>{bankCard?.bank_name}</span>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MerchantCenter;
